import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamicAds';
import WidgetStickyContents from 'src/components/widgets/translates/widget-sticky-contents';
import { APP_IMG, APP_URL, CONTENT_DEFAULT, COUNTVIEW_SHOW_COUNT, IS_NO_ROBOT_INDEX, NEXT_PUBLIC_URL_SOUND, NEXT_PUBLIC_WEB_NAME_SOUND } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_LOGO, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { IAds } from 'src/modules/ads';
import { DataContent, IAffiliate, Related } from 'src/modules/cid/id';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import AdsNative from 'src/utils/ads/ads-native';
import { setContents } from 'src/utils/contents/set-contents';
import { incrementCountview } from 'src/utils/countview';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import { isShouldFetch } from 'src/utils/detect-scrolling';
import { convertObjPath, escapeChar, rewriteTxt, timestamp } from 'src/utils/helper';
import { GET, GET_ARTICLES } from 'src/utils/service';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';
import { NAV_DATA } from 'src/redux/navSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/store';

const DynamicAds = dynamic(import('src/components/ads/dynamicAds'));
const EmptyArticle = dynamic(import('src/components/empty'));
const ArticleAudio = dynamic(import('src/components/article/article-audio'));
const ArticleVideo = dynamic(import('src/components/article/article-video'));
const ArticleDefault = dynamic(import('src/components/article/article-default'));
const Layout = dynamic(import('src/components/layouts'));
const SectionAffiliate = dynamic(import('src/components/sections/section-events/section-affiliate'));

type Props = {
  article: DataContent;
  related: Related[];
  affiliate: IAffiliate[];
  spanContent?: any;
  dataPopular?: any;
  isTranslated?: any;
  adsNative?: any;
  countViewCheck: number;
  navBar?: any;
  ads?: IAds[];
  agent?: any;
};

const PageDetail = ({ article, related, isTranslated, affiliate, spanContent, countViewCheck, adsNative, ads, dataPopular, agent }: Props) => {
  const data = useUser();
  const [isTranslate, setIsTranslate] = useState(false);
  const [isKeyPoint, setIsKeyPoint] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectLang, setSelectLang] = useState('TH');
  const [newsArticle, setNewsArticle] = useState({ ...article });
  const sessionSevId = data?.user ? decryptionClient(data?.user) : null;
  const [navBar, setNavBar] = useState<any[]>([]);
  if (isEmpty(article)) return <EmptyArticle sessionSevId={sessionSevId} />;
  const router = useRouter();
  const _article = {
    ...article,
    ...newsArticle,
    title: newsArticle?.title || article?.meta?.title || WEB_TITLE,
    description: article?.meta?.description || WEB_DESC,
    keyword: article?.meta?.keyword || WEB_KEYWORD,
    image_url: article?.meta?.og_social_image || IMAGE_DEFAULT
  };

  /* ----------------------------------- SEO ---------------------------------- */
  const articleTitleSeo = escapeChar(_article?.title) || WEB_TITLE;
  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;
  const [countView, setCountView] = useState(_article?.pageviews || 0);
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    /* ------------------------------ INCREAMENT_COUNTVIEW ----------------------------- */
    (async () => {
      try {
        const _pageviews: any = await incrementCountview(_article);
        setCountView(_pageviews);
      } catch (err: any) {
        console.error(`${timestamp()} ==========> CARD_FEATURE ERROR :`, err.message);
      }
    })();
    /* ------------------------------- SET_CONTENT ------------------------------ */
    setContents();
  }, []);

  /* -------------------------------- DATALAYER ------------------------------- */
  const nextSectionIndexRef: any = useRef(0);
  const sectionsVisibleRef: any = useRef([]);
  const sectionRef: any = useRef(null);
  const footerRef: any = useRef(false);
  // const localStorage: Storage;
  const loadMoreArticles = () => {
    try {
      // const getDataReadMore = JSON.parse(localStorage.getItem('_dataReadMore'));
      if (nextSectionIndexRef?.current < related.length) {
        if (isShouldFetch()) {
          const currentIndex = nextSectionIndexRef.current;
          const section = related[currentIndex];

          nextSectionIndexRef.current = currentIndex + 1;
          sectionsVisibleRef.current = [...sectionsVisibleRef.current, section];

          setTimeout(() => {
            loadMoreArticles();
          }, 1000);
        } else {
          document.addEventListener('scroll', handleScroll);
        }
      }
    } catch (err) {
      console.error(`${timestamp()}==========> INDEX ERROR : ${nextSectionIndexRef.current}`, err);
    }
  };

  const handleParagraphOne = async () => {
    const sectionEl: any = await document.getElementById('paragraph-1');

    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-1';

        document.removeEventListener('scroll', handleParagraphOne);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-1'
          }
        });
      }
    }
  };

  const handleParagraphTwo = async () => {
    const sectionEl: any = await document.getElementById('paragraph-2');

    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-2';
        document.removeEventListener('scroll', handleParagraphTwo);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-2'
          }
        });
      }
    }
  };

  const handleParagraphThree = async () => {
    const sectionEl: any = await document.getElementById('paragraph-3');
    if (sectionEl) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'paragraph-3';
        document.removeEventListener('scroll', handleParagraphThree);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'paragraph-3'
          }
        });
      }
    }
  };

  const handleScrollToReadComplete = async () => {
    try {
      const sectionEl: any = await document.getElementById('section-ads');
      if (sectionEl !== null) {
        if (sectionEl.offsetTop < document.documentElement.scrollTop) {
          // console.log(sectionEl);
          // console.log(localStorage.getItem('_t'), isNaN(localStorage.getItem('_t')));
          sectionRef.current = 'section-ads';
          const _t: any = localStorage.getItem('_t');
          if (!isNaN(_t)) {
            const counter = parseInt(_t);
            const toArray = (num: any) => num && [...`${num}`].map(elem => parseInt(elem));
            const _a = toArray(counter)[toArray(counter).length - 1] === 0 ? 0 : 1;
            const _b = counter - 1;
            const start = Math.floor((_a === 0 ? _b : counter) / 10) * 10;
            const end = Math.ceil(counter / 10) * 10;
            const first = start === 0 ? 1 : start + 1;
            const second = end === 0 ? 10 : end;
            const timer = `${first}-${second}`;

            document.removeEventListener('scroll', handleScrollToReadComplete);
            await sendDataLayerGlobal({
              type: DATALAYER_TYPE.TRACK_READTIME,
              router: router?.pathname,
              section: 'scroll',
              data: {
                title: router.asPath,
                heading: 'read-complete',
                time: timer
              }
            });
            await localStorage.removeItem('_t');
          } else {
            document.removeEventListener('scroll', handleScrollToReadComplete);
            await sendDataLayerGlobal({
              type: DATALAYER_TYPE.TRACK_READTIME,
              router: router?.pathname,
              section: 'scroll',
              data: {
                title: router.asPath,
                heading: 'read-complete',
                time: '1-10'
              }
            });
            await localStorage.removeItem('_t');
          }
        }
      }
    } catch (error) {
      console.log('Error: Please enable website cookies. ' + error);
    }
  };

  const handleScrollToSectionAds = async () => {
    const sectionEl: any = await document.getElementById('section-ads');
    if (sectionEl !== null) {
      if (sectionEl.offsetTop < document.documentElement.scrollTop) {
        sectionRef.current = 'section-ads';
        document.removeEventListener('scroll', handleScrollToSectionAds);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'section-ads'
          }
        });
        footerRef.current = true;
      }
    }
  };
  const handleScrollToSectionFooter = async () => {
    const sectionEl: any = await document.getElementById('footer-wrapper');
    if (footerRef.current) {
      if (sectionEl?.offsetTop < document.documentElement.scrollTop + 500) {
        document.removeEventListener('scroll', handleScrollToSectionFooter);
        await sendDataLayerGlobal({
          type: DATALAYER_TYPE.TRACK_SCROLL,
          router: router?.pathname,
          section: 'scroll',
          data: {
            title: router.asPath,
            heading: 'footer-wrapper'
          }
        });
      }
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem('_t', '0');
      const increment = () => {
        const count: any = localStorage.getItem('_t');
        const time: any = parseInt(count);
        localStorage.setItem('_t', time + 1);
      };
      setInterval(increment, 1000);
    } catch (error) {
      console.log('Error: Please enable website cookies. ' + error);
    }
  }, []);

  const handleScroll = () => {
    if (isShouldFetch()) {
      document.removeEventListener('scroll', handleScroll);
      loadMoreArticles();
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);

    return () => document.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    document.addEventListener('scroll', handleParagraphOne);
    return () => document.removeEventListener('scroll', handleParagraphOne);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleParagraphTwo);
    return () => document.removeEventListener('scroll', handleParagraphTwo);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleParagraphThree);
    return () => document.removeEventListener('scroll', handleParagraphThree);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToReadComplete);
    return () => document.removeEventListener('scroll', handleScrollToReadComplete);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToSectionAds);
    return () => document.removeEventListener('scroll', handleScrollToSectionAds);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', handleScrollToSectionFooter);
    return () => document.removeEventListener('scroll', handleScrollToSectionFooter);
  }, []);

  const handleTranslate = async (newLanguage: any) => {
    try {
      setSelectLang(newLanguage);
      setIsLoading(true);
      setIsTranslate(!isTranslate);
      setIsKeyPoint(false);
      if (NEXT_PUBLIC_URL_SOUND !== undefined || !isEmpty(article?.id)) {
        const uriToken = `${NEXT_PUBLIC_URL_SOUND}/create`;
        const getToken = await axios
          .post(
            uriToken,
            {
              app: 'default'
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'User-Agents': `NationSpeech/1.0 ${agent}`
              }
            }
          )
          .catch((error: any) => {
            console.error(error);
          });

        // console.log({ getToken });
        if (getToken?.data?.data?.token !== undefined) {
          const uriEn = `${NEXT_PUBLIC_URL_SOUND}/translates/${article?.id}/${newLanguage}`;
          // console.log('uriEn', uriEn);

          const translate = await axios
            .post(
              uriEn,
              {
                data: {
                  id: article?.id,
                  site: NEXT_PUBLIC_WEB_NAME_SOUND,
                  language: newLanguage
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'User-Agents': `NationSpeech/1.0 ${agent}`,
                  accessToken: getToken?.data?.data?.token,
                  accessTimeStamp: getToken?.data?.data?.timestamp
                }
              }
            )
            .catch(error => {
              console.error(error);
            });
          await new Promise(resolve => setTimeout(resolve, 500));
          setNewsArticle({
            ...article,
            ...translate?.data?.data
          });
        }
      }
    } catch (error) {
      setIsLoading(true);
      console.log(error, 'Error Translate ');
    } finally {
      setIsLoading(false);
    }
  };

  const navSubMenu = useAppSelector(props => props?.navData?.result?.[0]);
  const dispatch = useAppDispatch();

  // ---------- Fetch menu-nav จากฝั่ง client ----------
  useEffect(() => {
    let mounted = true;
    const fetchMenuNav = async () => {
      try {
        const res = await GET('/api/v1.0/navigations/menu-nav?limit=9');
        if (!mounted || isEmpty(res)) return;

        setNavBar(res);

        const find = res?.filter((i: any) => i.nameEng === 'read');
        if (!isEmpty(find)) {
          dispatch(NAV_DATA(find));
        }
      } catch (err) {
        console.error('fetch menu-nav error:', err);
      }
    };

    fetchMenuNav();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article?.title || WEB_TITLE}
        description={_article?.description || WEB_DESC}
        openGraph={{
          type: 'article',
          locale: 'en_US',
          site_name: WEB_NAME,
          title: _article?.title || WEB_TITLE,
          description: _article?.description || WEB_DESC,
          url: `${APP_URL}${_article?.link}`,
          article: {
            publishedTime: `${_article?.published_at}`,
            modifiedTime: `${_article?.updated_at}`,
            section: _article?.section?.th,
            authors: [`${WEB_URL.FACEBOOK}`],
            tags: _article?.tags
          },
          images: [
            {
              url: _article?.image_url || IMAGE_DEFAULT,
              secureUrl: _article?.image_url || IMAGE_DEFAULT,
              width: 700,
              height: 395,
              alt: _article?.title || WEB_TITLE
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={`${_article?.canonical}`} />
        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={_article?.image_url || IMAGE_DEFAULT} />
        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                    __th_page="${_article?.section?.en}";
                  `
          }}
        />
        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                    {
                      "@context": "https://schema.org",
                      "@type": "WebSite",
                      "name": "${WEB_NAME}",
                      "url": "${WEB_URL.WEB}",
                      "description": "${articleDescriptionSeo}",
                      "sameAs": [
                        "${WEB_URL.FACEBOOK}",
                        "${WEB_URL.YOUTUBE}",
                        "${WEB_URL.INSTAGRAM}",
                        "${WEB_URL.TWITTER}"
                      ],
                      "potentialAction": {
                        "@type": "SearchAction",
                        "target": "${WEB_URL.WEB}search?q={search_term_string}",
                        "query-input": "required name=search_term_string"
                      }
                    }
                  `
          }}
        />
        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "WebPage", 
                      "name": "${articleTitleSeo}", 
                      "description": "${articleDescriptionSeo}", 
                      "publisher": { 
                        "@type": "ProfilePage", 
                        "name": "${WEB_NAME}" 
                      } 
                    } 
                  `
          }}
        />
        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "BreadcrumbList", 
                      "itemListElement": [ 
                        { 
                          "@type": "ListItem", 
                          "position": 1, 
                          "name": "หน้าแรก", 
                          "item": "${APP_URL}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 2, 
                          "name": "${_article?.section?.th}", 
                          "item": "${APP_URL}/${_article?.section?.en}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 3, 
                          "name": "${articleTitleSeo}", 
                          "item": "${_article?.canonical || `${APP_URL}${_article?.link}`}"
                        } 
                      ] 
                    } 
                  `
          }}
        />
        {/* -------------------------- SNIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "NewsArticle", 
                      "mainEntityOfPage": { 
                        "@type": "WebPage", 
                        "@id": "${_article?.canonical || `${APP_URL}${_article?.link}`}"
                      }, 
                      "headline": "${articleTitleSeo}", 
                      "alternativeHeadline": "${articleTitleSeo}", 
                      "description": "${articleDescriptionSeo}", 
                      "image": "${`${APP_IMG}${_article?.image}` || _article?.image_url || IMAGE_DEFAULT}", 
                      "datePublished": "${_article?.published_at}",
                      "dateModified": "${_article?.updated_at}",
                      "author": { 
                        "@type": "Thing", 
                        "name": "${_article.byline || WEB_NAME}",
                        "url": "${APP_URL}"
                      }, 
                      "publisher": { 
                        "@type": "Organization", 
                        "name": "${WEB_NAME}", 
                        "logo": { 
                          "@type": "ImageObject", 
                          "url": "${APP_IMG}/static/${WEB_LOGO}" 
                        } 
                      } 
                    } 
                  `
          }}
        />
        {_article.is_show_ads && <script async src='https://www.googletagservices.com/tag/js/gpt.js' />}
      </Head>
      {!isEmpty(affiliate) && <SectionAffiliate position={4} data={affiliate} />}
      <Layout sessionSevId={sessionSevId} data={{ header: navBar, footer: navBar }}>
        <CategoryWrapper spanContent={spanContent}>
          {_article?.is_video && _article.type_video !== '5' && (
            <ArticleVideo article={article} _article={_article} countView={countView} countViewCheck={countViewCheck} related={related} dataAffiliate={affiliate} ads={ads} />
          )}
          {_article?.is_video && _article.type_video === '5' && (
            <ArticleAudio article={article} _article={_article} countView={countView} countViewCheck={countViewCheck} related={related} dataAffiliate={affiliate} ads={ads} />
          )}
          {!_article?.is_video && (
            <ArticleDefault
              childSticky={
                isTranslated === true && (
                  <WidgetStickyContents className='block-sticky' isLoading={isLoading} article={article} handleTranslate={handleTranslate} selectLang={selectLang} router={router} />
                )
              }
              article={article}
              _article={_article}
              countView={countView}
              countViewCheck={countViewCheck}
              related={related}
              dataAffiliate={affiliate}
              ads={ads}
              dataPopular={dataPopular}
              isKeyPoint={isKeyPoint}
            />
          )}
          {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
        </CategoryWrapper>
        {/* ------------ Ads : infinities -------------- */}
        <div className='loadmore' />
        {article?.is_show_ads && <AdsNative data={adsNative || null} isShowAds={article?.is_show_ads} />}
      </Layout>

      {/* --------------------------- Social embed script -------------------------- */}
      <Script strategy='lazyOnload' src='//www.instagram.com/embed.js' />
      <Script strategy='lazyOnload' src='https://platform.twitter.com/widgets.js' />

      {/* -------------------------- ADS Default -------------------------- */}
      {/* {_article.is_show_ads && (
        <Script
          strategy='lazyOnload'
          src='https://compasscdn.adop.cc/js/adop_new_collapse_width100_0.0.1.js'
          data-id='ADOP_V_3vsZsppzdJ'
          data-width='300'
          data-height='250'
          data-type='re'
          data-zone='9961fd53-5881-413d-b537-875b9ef13185'
          data-fl='Y'
          data-margin='0,80,0,0'
          data-fl-width='300'
          data-loc='rb'
        ></Script>
      )} */}
      {_article.is_show_ads && <Script strategy='lazyOnload' src='//get.optad360.io/sf/a66a991c-b2f5-4b82-b747-b1c39b8978f8/plugin.min.js' />}

      {/* {_article.is_show_ads && (
        <Script
          strategy='lazyOnload'
          dangerouslySetInnerHTML={{
            __html: `
          var adoptagdfp = 'ZXZhbChmdW5jdGlvbihwLGEsYyxrLGUsZCl7ZT1mdW5jdGlvbihjKXtyZXR1cm4oYzxhPycnOmUocGFyc2VJbnQoYy9hKSkpKygoYz1jJWEpPjM1P1N0cmluZy5mcm9tQ2hhckNvZGUoYysyOSk6Yy50b1N0cmluZygzNikpfTtpZighJycucmVwbGFjZSgvXi8sU3RyaW5nKSl7d2hpbGUoYy0tKXtkW2UoYyldPWtbY118fGUoYyl9az1bZnVuY3Rpb24oZSl7cmV0dXJuIGRbZV19XTtlPWZ1bmN0aW9uKCl7cmV0dXJuJ1xcdysnfTtjPTF9O3doaWxlKGMtLSl7aWYoa1tjXSl7cD1wLnJlcGxhY2UobmV3IFJlZ0V4cCgnXFxiJytlKGMpKydcXGInLCdnJyksa1tjXSl9fXJldHVybiBwfSgnNCA1PTV8fHt9OzUubD01Lmx8fFtdOzQgbT1tfHxDKGMsZCxnLGIpezQgZj0hMCxoPSIvL0IuRi5vL3EvIitiLGU9IkgiK2kuQSh5KmkuRCgpKSsxOy0xIT1iLkcoIi8vIikmJihmPSExKTs0IGE9My5FKCJ4Iik7YS4yKCJ3IixlKTthLjIoInAiLCJuIik7YS4yKCJyIiwwKTthLjIoInMiLDApO2EuMigidiIsMCk7YS4yKCJ1IiwwKTthLjIoInQiLDApO2EuMigieiIsZCk7YS4yKCJaIixnKTthLjIoIlgiLCJJIik7ZD0iPDkgaz1cJyIraCsiXCc+Vi85PiI7aihjPTMuNihjKSl7VShjLjIoIlkiLCJXIik7Yy5TKCk7KWMuTShjLlQpO2MuTChhKX1LIDggNztqKCJKIj09Yik4IDc7Zj8oYT0zLjYoZSksYj1hLk58fGEuTy4zLDchPWImJihiLlIoKSxiLlEoZCksYi5QKCkpKTooYT0zLjYoZSksYS5rPWIpfTsnLDYyLDYyLCd8fHNldEF0dHJpYnV0ZXxkb2N1bWVudHx2YXJ8Z29vZ2xldGFnfGdldEVsZW1lbnRCeUlkfG51bGx8cmV0dXJufHNjcmlwdHx8fHx8fHx8fE1hdGh8aWZ8c3JjfGNtZHxhZG9wQURzaG93fG5vbmV8Y2N8Ym9yZGVyfFJFfGZyYW1lQm9yZGVyfG1hcmdpbldpZHRofHBhZGRpbmdIZWlnaHR8cGFkZGluZ1dpZHRofG1hcmdpbkhlaWdodHxpZHxJRlJBTUV8MUU0fHdpZHRofGZsb29yfGNvbXBhc3N8ZnVuY3Rpb258cmFuZG9tfGNyZWF0ZUVsZW1lbnR8YWRvcHxpbmRleE9mfGFkb3BCfG5vfEFET1BfUEJVfGVsc2V8YXBwZW5kQ2hpbGR8cmVtb3ZlQ2hpbGR8Y29udGVudERvY3VtZW50fGNvbnRlbnRXaW5kb3d8Y2xvc2V8d3JpdGV8b3BlbnxoYXNDaGlsZE5vZGVzfGZpcnN0Q2hpbGR8Zm9yfDx8YWRzYnlhZG9wX3xzY3JvbGxpbmd8Y2xhc3N8aGVpZ2h0Jy5zcGxpdCgnfCcpLDAse30pKTs='
          eval(atob(adoptagdfp));
        `
          }}
        />
      )} */}
      {/* {_article.is_show_ads && <Script strategy='lazyOnload' src='https://compass.adop.cc/ST/b83d6b73-a283-4dd7-83a1-815bcb6d5dc7' />} */}
      {/* //<!-- Mobile Interstitial --> */}

      {/* <!-- Combination Unit in GPT --> */}
      {/* {_article.is_show_ads && (
        <Script
          strategy='lazyOnload'
          dangerouslySetInnerHTML={{
            __html: `//   var timerA; // Sticky
          //   var slotNameA='div-apt-ad-dcmzOpwNzC';
          //   var passbackurlA='b5d716df-ea4e-42a7-863b-00dca39d59ff';
          //   var wA=320,hA=100;
            
            var timerB; // R_C_T
            var slotNameB='div-apt-ad-LTBWhLlDur';
            var passbackurlB='6f7360ed-01ba-4c2b-8347-918ce068f47a';
            var wB=336,hB=280;

            var timerC; // R_C_M
            var slotNameC='div-apt-ad-wv32mDfCrx';
            var passbackurlC='250dcdc5-e16b-46a5-8711-03105323a671';
            var wC=336,hC=280;

            googletag.cmd.push(function() {
              // var gptUnitA = googletag.defineSlot('/223513049/adopthailand/thepeople_m_320x100_sticky', [[320, 50], [320, 100], [300, 100], [728, 90]], slotNameA).addService(googletag.pubads());
              // var mappingSizeA = googletag.sizeMapping()
              //                 .addSize([720, 0], [0, 0])
              //                 .addSize([0, 0], [[320, 100], [320, 50], [300, 100]])
              //                 .build();
              // gptUnitA.defineSizeMapping(mappingSizeA);
              
              var gptUnitB = googletag.defineSlot('/223513049/adopthailand/thepeople_r_content_t_728x90_336x280', [[320, 250], [320, 480], [336, 280], [336, 380], [480, 320], [468, 60], [728, 90], [300, 250]], slotNameB).addService(googletag.pubads());
              var mappingSizeB = googletag.sizeMapping()
                              .addSize([720, 0], [[468, 60], [480, 320], [728, 90], [336, 280]])
                              .addSize([0, 0], [[250, 250], [300, 250], [320, 480], [336, 280]])
                              .build();
              gptUnitB.defineSizeMapping(mappingSizeB);

              var gptUnitC = googletag.defineSlot('/223513049/adopthailand/thepeople_r_content_m_728x90_336x280', [[320, 250], [320, 480], [336, 280], [336, 380], [480, 320], [468, 60], [728, 90], [300, 250]], slotNameC).addService(googletag.pubads());
              var mappingSizeC = googletag.sizeMapping()
                              .addSize([720, 0], [[468, 60], [480, 320], [728, 90], [336, 280]])
                              .addSize([0, 0], [[250, 250], [300, 250], [320, 480], [336, 280]])
                              .build();
              gptUnitC.defineSizeMapping(mappingSizeC);

              googletag.pubads().set("page_url", "stag.thepeople.co | dev.thepeople.co | thepeople.co");
              googletag.pubads().enableLazyLoad({
                      fetchMarginPercent: 40,
                      renderMarginPercent: 10,
                      mobileScaling: 1.5
              });
              googletag.pubads().addEventListener('slotVisibilityChanged', function(adopSlot) {
                adopSlot.slot.getSlotElementId();
                var slot = adopSlot.slot;
                view = adopSlot.inViewPercentage;
              //   if (slot === gptUnitA && view<=55) { window.clearTimeout(timerA); }
                if (slot === gptUnitB && view<=55) { window.clearTimeout(timerB); }
                if (slot === gptUnitC && view<=55) { window.clearTimeout(timerC); }
              });
              googletag.pubads().addEventListener('slotRenderEnded', function(adopSlot) {
                  var thistag = render = adopSlot.slot.getSlotElementId();
                  var timer=30000;
                  // if(thistag===slotNameA) { timer=16000; name="Sticky"; }
                  if(thistag===slotNameB) { name="R_C_T"; }
                  if(thistag===slotNameC) { name="R_C_M"; }
                  setTimeout( function(){
                      googletag.pubads().refresh([adopSlot.slot]);
                  }, 20000);
                  if(adopSlot.isEmpty){
                  //   if(thistag===slotNameA) { adopADshow(slotNameA,wA,hA,passbackurlA); } // sticky
                    if(thistag===slotNameB) { adopADshow(slotNameB,wB,hB,passbackurlB); } // r_c_t
                    if(thistag===slotNameC) { adopADshow(slotNameC,wC,hC,passbackurlC); } // r_c_m
                    render = thistag + " // Passback";
                  } console.log("Rendered : " + render);
              });
              googletag.enableServices();
            });`
          }}
        />
      )} */}
    </>
  );
};

const CategoryWrapper: any = styled.section`
  position: relative;
  padding: 30px 0;
  .content-detail p {
    margin: 5px auto;
  }
  .block-sticky {
    display: block;
    text-align: center;
    /* @media (max-width: 768px) {
        width: 0;
      } */
  }
  .ads-billboard {
    @media (max-width: 768px) {
      min-height: 120px;
    }
  }
  .content-detail {
    padding-left: 5px;
    span[style='font-weight: 400; font-family: csprajad; font-size: 14pt;'],
    span[style='font-weight: 400; font-size: 14pt; font-family: csprajad;'],
    span[style='font-family: csprajad; font-size: 14pt;'],
    span[style='font-size: 14pt; font-family: csprajad;'] {
      ${({ spanContent }: any) => (spanContent ? 'margin:15px auto; display:block;' : '')}
    }
    h2 {
      display: block;
      margin: auto;
      font-size: 18px;
      font-weight: 500;
      color: rgb(35, 35, 35);
      width: 95%;
      line-height: 1.8;
      border-left: 8px solid #ea212d;
      background: linear-gradient(90deg, #f8f8f8 85%, white 100%);
      padding: 15px 15px 15px 35px;
      border-radius: 4px;
    }
    a {
      @media (max-width: 480px) {
        padding: 20px 0;
      }
      &:hover {
        color: rgb(237, 26, 59);
      }
    }
    img {
      object-fit: cover;
      width: 100%;
    }
  }
  .card-item {
    .card.--content h3 {
      font-size: 16px;
    }
  }

  .twitter-tweet {
    margin: auto;
  }

  blockquote {
    @media (max-width: 480px) {
      margin: 0;
    }
  }

  blockquote {
    position: relative;
    max-width: 700px;
    width: 100%;
    padding: 30px;
    margin: 10px auto;
    color: #111;
    font-size: 28px;
    font-weight: 700px;
    text-align: center;
    font-family: 'Utopia-italic';
    @media (max-width: 768px) {
      font-size: 20px;
    }
    p {
      display: block;
      color: #ea212d;
    }
    &:before {
      position: absolute;
      content: '‘';
      color: #ea212d;
      font: 700 15em/2em sans-serif;
      opacity: 20%;
      top: -0.5em;
      left: -0.05em;
      right: unsets;
    }
    &::after {
      position: absolute;
      content: '‘';
      color: #ea212d;
      font: 700 15em/2em sans-serif;
      opacity: 10%;
      top: -0.5em;
      left: 0.15em;
      right: unset;
    }
    @media (max-width: 768px) {
      padding: 15px 20px;
    }
  }

  // iframe dailymotion
  div[style='position:relative;padding-bottom:56.25%;height:0;overflow:hidden;'] > iframe {
    display: block;
    position: relative !important;
    margin: 20px auto !important;
    max-width: 100% !important;
    height: 480px;
    @media (max-width: 767px) {
      height: 190px !important;
    }
  }
  div[style='position:relative;padding-bottom:56.25%;height:0;overflow:hidden;'] {
    height: 480px !important;
    @media (max-width: 767px) {
      height: 200px !important;
    }
  }
  // iframe dailymotion end
`;

export const getServerSideProps: GetServerSideProps = async ({ req, params, query, res }: any) => {
  const initProps: any = {
    statusCode: 200,
    errMessage: null,
    article: [],
    related: [],
    dataPopular: [],
    affiliate: [],
    spanIdDefaultContent: CONTENT_DEFAULT,
    countViewCheck: COUNTVIEW_SHOW_COUNT,
    spanContent: false,
    adsNative: {},
    isTranslated: false,
    navBar: [],
    ads: [],
    agent: null
  };
  const { expires, signature } = query;

  const _type = `/${query.type || ''}`;
  let preview = '';
  let previewParams = {};
  if (expires && signature) {
    preview = '/preview';
    previewParams = {
      expires,
      signature
    };
  }

  const url = req.url;
  let uri = `contents/${rewriteTxt(params.id)}${preview}`;
  if (url.startsWith('/advertorials')) {
    uri = `contents/${params.id}${_type}${preview}`;
  } else if (url.startsWith('/pr-news')) {
    uri = `contents/${params.id}${_type}${preview}`;
  } else if (url.startsWith('/blogs')) {
    uri = `contents/${params.id}${_type}${preview}`;
  }

  if (rewriteTxt(params.id) === 'latest-news') {
    return {
      redirect: {
        destination: `${APP_URL}/category/latest-news`,
        permanent: false
      }
    };
  }

  try {
    // ยิง API ทั้ง 3 ตัวพร้อมกัน (parallel) แทนที่จะรอทีละตัว
    // ใช้ allSettled เพื่อไม่ให้ navBar/popular ที่ fail ทำให้ article fail ไปด้วย
    const [popularNewsResult, articleResult] = await Promise.allSettled([
      GET('/api/v1.0/categories/popular-news'),
      GET_ARTICLES(`/api/v1.0/${uri}${convertObjPath({ ...previewParams })}`, false, true)
    ]);

    // จัดการ menuNav

    // จัดการ popularNews
    if (popularNewsResult.status === 'fulfilled' && !isEmpty(popularNewsResult.value)) {
      initProps.dataPopular = popularNewsResult.value;
    }

    // จัดการ article (ตัวหลัก — ถ้า fail ต้อง throw เพื่อเข้า catch block)
    if (articleResult.status === 'rejected') {
      throw articleResult.reason;
    }

    const resData = articleResult.value;

    if (resData?.statusCode === 404 || isUndefined(resData)) {
      res.setHeader('location', '/');
      res.statusCode = 301;
      res.end();
      // return {
      //   notFound: true
      // };
    }

    if (!isEmpty(resData?.data)) {
      initProps.article = resData?.data || [];
      initProps.related = resData?.related ? resData?.related.slice(0, 5) : [];
      initProps.affiliate = resData?.affiliate || [];
      initProps.adsNative = resData?.adsNative || [];
      initProps.ads = resData?.ads || [];
      initProps.spanContent = params.id < initProps.spanIdDefaultContent;
      initProps.ads = resData?.ads;
      initProps.isTranslated = resData?.data?.is_translate ?? false;
      initProps.agent = req.headers['user-agent'];
    }
  } catch (err: any) {
    console.error(`${timestamp()} ==========> ARTICLE_DETAIL ERROR : `, err.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
};

export default PageDetail;
